import { Injectable } from "@angular/core";
import { LocalStorage } from "@common/lib/storage/local-storage";
import { BaseInitialisationService } from "@common/service/base-initialisation.service";
import { UrlFilterService } from "@common/shell/filter/url-filter.service";
import { OrganisationMapSupplementaryData } from "@org-common/lib/architecture/organisation-map/organisation-map-supplementary-data.enum";
import isEqual from "lodash.isequal";
import { BehaviorSubject } from "rxjs";

export interface IOrganisationMapFilterParams {
    showMyTeams: boolean;
    showPrivateTeams: boolean;
    showEndedTeams: boolean;
    view: number;
    supplementaryData: OrganisationMapSupplementaryData[];
}

export const OrganisationMapFilterParamKeys: (keyof IOrganisationMapFilterParams)[] = [
    "showMyTeams",
    "showPrivateTeams",
    "showEndedTeams",
    "view",
    "supplementaryData",
];

export const DefaultOrganisationMapFilter: IOrganisationMapFilterParams = {
    showMyTeams: false,
    showPrivateTeams: false,
    showEndedTeams: false,
    view: 2,
    supplementaryData: [],
};

@Injectable({
    providedIn: "root",
})
export class OrganisationMapFilterService extends BaseInitialisationService {
    private static readonly storageKey = "OrganisationMapFilter";

    public filter$ = new BehaviorSubject<IOrganisationMapFilterParams>({ ...DefaultOrganisationMapFilter });

    public constructor(
        private urlFilterService: UrlFilterService,
    ) {
        super();
    }

    public get isDefault() {
        // exclude view from filter default since it's not really a filter option
        const { view, ...filter } = this.filter$.value;
        const { view: _, ...defaultFilter } = DefaultOrganisationMapFilter;
        return isEqual(filter, defaultFilter);
    }

    public reset() {
        // keep the original view value
        const { view } = this.filter$.value;
        this.filter$.next({ ...DefaultOrganisationMapFilter, view });
        LocalStorage.delete(OrganisationMapFilterService.storageKey);
    }

    public async updateFilter(filterParams: IOrganisationMapFilterParams) {
        if (!isEqual(this.filter$.value, filterParams)) {
            this.filter$.next(filterParams);
            LocalStorage.set(OrganisationMapFilterService.storageKey, filterParams);
            await this.setFilterToUrl();
        }
    }

    public setFilterFromLocalStorage() {
        const currentFilter = LocalStorage.get<IOrganisationMapFilterParams>(OrganisationMapFilterService.storageKey);
        const normalisedFilter = currentFilter
            ? { ...DefaultOrganisationMapFilter, ...currentFilter }
            : undefined;
        if (normalisedFilter && !isEqual(this.filter$.value, normalisedFilter)) {
            this.filter$.next(normalisedFilter);
        }
    }

    public async setInitialFilter() {
        this.setFilterFromLocalStorage();
        this.setFilterFromUrl();
        LocalStorage.set(OrganisationMapFilterService.storageKey, this.filter$.value);
        await this.setFilterToUrl();
    }

    public setFilterFromUrl() {
        const currentFilter = this.urlFilterService.getFiltersFromUrl<IOrganisationMapFilterParams>(
            OrganisationMapFilterParamKeys,
            this.filter$.value,
        );

        if (!isEqual(this.filter$.value, currentFilter)) {
            this.filter$.next(currentFilter);
        }
    }

    public setFilterToUrl() {
        return this.urlFilterService.setFilterToUrl<IOrganisationMapFilterParams>(
            OrganisationMapFilterParamKeys,
            this.filter$.value,
            DefaultOrganisationMapFilter,
        );
    }
}
